import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, ExternalLink, GripVertical, Plus } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { formatCurrency } from '@/lib/utils';
import { subtreeVisible } from './workbench-model';

/**
 * One row of the chart-of-accounts workbench.
 *
 * The tree the accountant is already looking at IS the editor: double-click a
 * cell to edit it in place, drag the handle to re-parent, tick the box to add it
 * to a selection. The row never navigates away, so their place in a 1,358-row
 * chart survives every edit.
 */

export interface WorkbenchNode {
  id: string;
  code: string;
  name: string;
  type: string;
  subType: string | null;
  balance: number;
  subtreeBalance?: number;
  currency: string;
  isActive: boolean;
  isSystemAccount: boolean;
  journalLineCount: number;
  children: WorkbenchNode[];
}

export type EditableField = 'code' | 'name' | 'type';

export interface RowCallbacks {
  onToggleExpand: (code: string) => void;
  onToggleSelect: (id: string, shiftKey: boolean) => void;
  onCommit: (id: string, field: EditableField, value: string) => void;
  onOpen: (id: string) => void;
  onAddChild: (node: WorkbenchNode) => void;
  onDrop: (draggedId: string, targetId: string) => void;
}

interface WorkbenchTreeRowProps extends RowCallbacks {
  node: WorkbenchNode;
  depth: number;
  expanded: Set<string>;
  selected: Set<string>;
  /** When set, only these ids render — used by the health-panel filters. */
  visibleIds: Set<string> | null;
  typeColors: Record<string, string>;
  editing: { id: string; field: EditableField } | null;
  onEdit: (cell: { id: string; field: EditableField } | null) => void;
  savingId: string | null;
}

const TYPE_VALUES = ['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE'];

export function WorkbenchTreeRow({
  node,
  depth,
  expanded,
  selected,
  visibleIds,
  typeColors,
  editing,
  onEdit,
  savingId,
  ...callbacks
}: WorkbenchTreeRowProps) {
  const { t } = useTranslation('accounting');
  const [dragOver, setDragOver] = useState(false);

  const hasChildren = node.children.length > 0;
  const isExpanded = expanded.has(node.code);
  const isSelected = selected.has(node.id);
  const isSaving = savingId === node.id;
  const displayedBalance = node.subtreeBalance ?? node.balance;
  // An account with no postings can be changed with no consequences at all.
  const isFree = node.journalLineCount === 0;

  if (!subtreeVisible(node, visibleIds)) return null;

  const editCell = (field: EditableField) => onEdit({ id: node.id, field });
  const isEditing = (field: EditableField) => editing?.id === node.id && editing.field === field;

  return (
    <>
      <tr
        className={`group border-border/60 border-b ${isSelected ? 'bg-primary/5' : 'hover:bg-muted/30'} ${
          dragOver ? 'ring-primary ring-2 ring-inset' : ''
        } ${!node.isActive ? 'opacity-55' : ''} ${isSaving ? 'animate-pulse' : ''}`}
        draggable
        onDragStart={(event) => event.dataTransfer.setData('text/plain', node.id)}
        onDragOver={(event) => {
          event.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragOver(false);
          const draggedId = event.dataTransfer.getData('text/plain');
          if (draggedId && draggedId !== node.id) callbacks.onDrop(draggedId, node.id);
        }}
      >
        <td className="w-8 px-2 py-1">
          <Checkbox
            checked={isSelected}
            onCheckedChange={(_checked, event) =>
              callbacks.onToggleSelect(node.id, event?.shiftKey === true)
            }
            aria-label={`${node.code} ${node.name}`}
          />
        </td>

        {/* Code */}
        <td className="whitespace-nowrap px-2 py-1 font-mono text-sm">
          <span className="inline-flex items-center" style={{ paddingLeft: `${depth * 18}px` }}>
            <GripVertical className="text-muted-foreground/40 mr-0.5 h-3.5 w-3.5 cursor-grab opacity-0 group-hover:opacity-100" />
            {hasChildren ? (
              <button
                className="hover:bg-muted mr-1 flex-shrink-0 rounded p-0.5"
                onClick={() => callbacks.onToggleExpand(node.code)}
                aria-label={isExpanded ? t('workbench.collapse') : t('workbench.expand')}
              >
                {isExpanded ? (
                  <ChevronDown className="text-muted-foreground h-3.5 w-3.5" />
                ) : (
                  <ChevronRight className="text-muted-foreground h-3.5 w-3.5" />
                )}
              </button>
            ) : (
              <span className="mr-1 inline-block w-[18px]" />
            )}

            {isEditing('code') ? (
              <InlineInput
                initial={node.code}
                mono
                onCommit={(value) => callbacks.onCommit(node.id, 'code', value)}
                onCancel={() => onEdit(null)}
              />
            ) : (
              <button
                className="hover:bg-muted/60 rounded px-1 text-left"
                onDoubleClick={() => editCell('code')}
                title={t('workbench.doubleClickToEdit')}
              >
                {node.code}
              </button>
            )}
          </span>
        </td>

        {/* Name */}
        <td className="px-2 py-1">
          {isEditing('name') ? (
            <InlineInput
              initial={node.name}
              onCommit={(value) => callbacks.onCommit(node.id, 'name', value)}
              onCancel={() => onEdit(null)}
            />
          ) : (
            <button
              className={`hover:bg-muted/60 w-full rounded px-1 text-left text-sm ${
                hasChildren ? 'text-muted-foreground' : 'font-medium'
              }`}
              onDoubleClick={() => editCell('name')}
              title={t('workbench.doubleClickToEdit')}
            >
              {node.name}
              {!node.isActive && (
                <span className="text-muted-foreground ml-2 text-xs">
                  {t('workbench.inactive')}
                </span>
              )}
            </button>
          )}
        </td>

        {/* Type */}
        <td className="px-2 py-1">
          {isEditing('type') ? (
            <select
              autoFocus
              defaultValue={node.type}
              className="border-border bg-background rounded border px-1 py-0.5 text-xs"
              onBlur={() => onEdit(null)}
              onChange={(event) => callbacks.onCommit(node.id, 'type', event.target.value)}
            >
              {TYPE_VALUES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          ) : (
            <button
              className={`rounded px-2 py-0.5 text-xs font-medium ${typeColors[node.type] ?? ''}`}
              onDoubleClick={() => editCell('type')}
              title={t('workbench.doubleClickToEdit')}
            >
              {node.type}
            </button>
          )}
        </td>

        {/* Balance — read-only: it is derived from postings, never typed */}
        <td className="px-2 py-1 text-right font-mono text-sm tabular-nums">
          {displayedBalance !== 0 ? (
            <span className={displayedBalance < 0 ? 'text-red-600 dark:text-red-400' : ''}>
              {formatCurrency(displayedBalance, node.currency)}
            </span>
          ) : (
            <span className="text-muted-foreground/40">-</span>
          )}
        </td>

        {/* Postings — the "is this safe to change" signal */}
        <td className="px-2 py-1 text-right font-mono text-xs tabular-nums">
          {isFree ? (
            <span className="text-muted-foreground/40">-</span>
          ) : (
            <span className="text-muted-foreground" title={t('workbench.hasPostings')}>
              {node.journalLineCount}
            </span>
          )}
        </td>

        <td className="w-16 px-2 py-1">
          <div className="flex items-center justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100">
            <button
              className="hover:bg-muted rounded p-1"
              onClick={() => callbacks.onAddChild(node)}
              title={t('workbench.addChild')}
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
            <button
              className="hover:bg-muted rounded p-1"
              onClick={() => callbacks.onOpen(node.id)}
              title={t('workbench.openDetail')}
            >
              <ExternalLink className="text-muted-foreground h-3.5 w-3.5" />
            </button>
          </div>
        </td>
      </tr>

      {hasChildren &&
        (isExpanded || visibleIds) &&
        node.children.map((child) => (
          <WorkbenchTreeRow
            key={child.id}
            node={child}
            depth={depth + 1}
            expanded={expanded}
            selected={selected}
            visibleIds={visibleIds}
            typeColors={typeColors}
            editing={editing}
            onEdit={onEdit}
            savingId={savingId}
            {...callbacks}
          />
        ))}
    </>
  );
}

/**
 * An input that commits on Enter or blur and reverts on Escape — the behaviour
 * a spreadsheet has, which is what this screen is standing in for.
 */
function InlineInput({
  initial,
  mono,
  onCommit,
  onCancel,
}: {
  initial: string;
  mono?: boolean;
  onCommit: (value: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initial);
  const ref = useRef<HTMLInputElement>(null);
  // Escape must not also fire the blur-commit below.
  const cancelled = useRef(false);

  useEffect(() => {
    ref.current?.select();
  }, []);

  return (
    <input
      ref={ref}
      value={value}
      autoFocus
      className={`border-primary bg-background w-full rounded border px-1 py-0.5 text-sm ${
        mono ? 'font-mono' : ''
      }`}
      onChange={(event) => setValue(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          onCommit(value.trim());
        } else if (event.key === 'Escape') {
          event.preventDefault();
          cancelled.current = true;
          onCancel();
        }
      }}
      onBlur={() => {
        if (cancelled.current) return;
        if (value.trim() && value.trim() !== initial) onCommit(value.trim());
        else onCancel();
      }}
    />
  );
}
